"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type RefObject, type ReactNode } from "react";
import { cn } from "@/lib/utils";

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0 transition-all duration-[650ms] ease-out",
        fromClasses[from],
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

type NbfcProduct = {
  n: string;
  title: string;
  body: string;
  img: string;
};

type NbfcStep = {
  n: string;
  title: string;
  body: string;
};

type NbfcFaq = {
  q: string;
  a: string;
};

type Advantage = {
  title: string;
  desc: string;
};

const HERO_TAGS = ["Quick Disbursement", "Flexible Eligibility", "Specialized Products"];

const ADVANTAGES: Advantage[] = [
  { title: "Faster Processing", desc: "Approvals in 48–72 hours vs. 3–6 weeks for traditional bank loans" },
  { title: "Flexible Eligibility", desc: "Assessment based on business potential and revenue, not just CIBIL score" },
  { title: "Minimal Documentation", desc: "Streamlined paperwork — get funded without drowning in forms" },
  { title: "Industry-Specific Products", desc: "Specialized financing for retail, manufacturing, services and tech" },
  { title: "No Collateral Required", desc: "Many NBFC products are unsecured or revenue-based" },
  { title: "Business-Friendly Terms", desc: "Flexible repayment aligned with your cash flow cycles" },
];

const PRODUCTS: NbfcProduct[] = [
  {
    n: "01",
    title: "Express Business Loans",
    body: "Quick-disbursement loans with minimal documentation, fast credit assessment and approval within 48 hours for eligible businesses.",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80",
  },
  {
    n: "02",
    title: "Working Capital Financing",
    body: "Flexible short-term credit facilities to manage inventory, operational expenses and daily cash flow gaps efficiently.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
  },
  {
    n: "03",
    title: "Business Expansion Loans",
    body: "Structured financing for scaling operations, opening new locations, purchasing equipment, or entering new markets.",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80",
  },
  {
    n: "04",
    title: "Unsecured Business Loans",
    body: "Collateral-free funding based on business revenue, cash flow and bank statement analysis — no property pledge required.",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80",
  },
  {
    n: "05",
    title: "Revenue-Based Financing",
    body: "Repayments linked to a fixed percentage of monthly revenue — paying more when business is strong, less during slower periods.",
    img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=600&q=80",
  },
  {
    n: "06",
    title: "Merchant Cash Advances",
    body: "Upfront lump-sum funding repaid as a percentage of daily card or digital payment sales — ideal for retail and F&B businesses.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80",
  },
  {
    n: "07",
    title: "Equipment Financing",
    body: "Dedicated loans for purchasing machinery, commercial vehicles, technology and business equipment with asset-backed security.",
    img: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80",
  },
  {
    n: "08",
    title: "Supply Chain Financing",
    body: "Invoice discounting, purchase order financing and vendor payment solutions to optimise your supply chain cash flow.",
    img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80",
  },
];

const ELIGIBILITY = [
  "Business operating for 6 months or more with documented revenue",
  "Consistent monthly revenue and active business bank account",
  "Basic financial records and bank statements available",
  "Clear business model with demonstrable growth potential",
  "Flexible credit score requirements — much lower than traditional banks",
  "Valid business registration — Pvt Ltd, LLP, Proprietorship, Partnership",
  "Industry-specific licences and permits where applicable",
];

const STEPS: NbfcStep[] = [
  { n: "01", title: "Requirement Analysis", body: "We understand your funding need, loan amount and urgency to define the right NBFC product." },
  { n: "02", title: "Financial Assessment", body: "We review bank statements, revenue trends and business profile to establish your credit case." },
  { n: "03", title: "NBFC Matching", body: "We identify 3–5 best-fit NBFC partners from our network for your specific business profile." },
  { n: "04", title: "Application Preparation", body: "We compile and prepare a strong, complete application package tailored to each lender." },
  { n: "05", title: "Priority Submission", body: "We submit through our priority channels with our NBFC partners for faster processing." },
  { n: "06", title: "Approval Facilitation", body: "We coordinate with NBFC underwriters directly to accelerate and support the approval process." },
  { n: "07", title: "Rapid Disbursement", body: "We finalise documentation and facilitate quick fund release — most clients funded within 5–7 days." },
];

const FAQS: NbfcFaq[] = [
  { q: "What are the main advantages of NBFC financing over bank loans?", a: "Faster approvals (48–72 hours vs. 3–6 weeks), flexible eligibility based on revenue rather than just CIBIL, minimal documentation and specialized products for unique business needs." },
  { q: "Do NBFC loans require collateral?", a: "Most NBFC products are unsecured or revenue-based. Some may require a personal guarantee or minimal collateral for larger amounts." },
  { q: "What is the interest rate range for NBFC loans?", a: "NBFC interest rates typically range from 12% to 24% per annum depending on loan amount, tenure and business risk profile." },
  { q: "What documents are needed for NBFC financing?", a: "Minimal documentation: PAN, Aadhaar, 6 months bank statements and business proof. Additional documents may be required based on loan type." },
  { q: "How quickly can I receive funds after approval?", a: "Most clients receive funds within 5–7 business days of complete document submission. Express products may disburse within 48 hours." },
  { q: "Can I apply if my CIBIL score is below 650?", a: "Yes. NBFCs assess creditworthiness based on revenue, cash flow and business performance — not just CIBIL. We will identify the best-fit lenders for your profile." },
];

function Divider() {
  return <div className="w-11 h-[3px] bg-[#1565C0] mb-6" />;
}

function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block", className)}>{children}</span>;
}

function AdvantageItem({ advantage, index }: { advantage: Advantage; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "flex gap-4 items-start py-4 transition-all duration-500 ease-out",
        index < ADVANTAGES.length - 1 ? "border-b border-[#E2E8F0]" : "",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"
      )}
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0] text-white flex-shrink-0">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div>
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1.5">{advantage.title}</h3>
        <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{advantage.desc}</p>
      </div>
    </div>
  );
}

function ProductCard({ product, index }: { product: NbfcProduct; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <article
      ref={ref}
      className={cn(
        "group flex flex-col overflow-hidden rounded-sm border border-[#E2E8F0] bg-white shadow-[0_1px_4px_rgba(0,0,0,0.04)] transition-all duration-500 ease-out hover:border-[#CBD5E1] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)]",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="relative h-[160px] overflow-hidden">
        <Image
          src={product.img}
          alt={product.title}
          fill
          className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 rounded-sm bg-[#1565C0] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          {product.n}
        </span>
      </div>
      <div className="flex flex-col p-[22px] pb-[26px]">
        <h3 className="text-[14px] font-semibold text-[#0F172A] mb-2 leading-[1.3]">{product.title}</h3>
        <p className="text-[13.5px] leading-[1.78] text-[#64748B]">{product.body}</p>
      </div>
    </article>
  );
}

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-transparent border-none py-5 flex items-center justify-between gap-4 text-left cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <span
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200",
            open ? "bg-[#1565C0]" : "bg-[#F8FAFC]"
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={open ? "M2 6h8" : "M6 2v8M2 6h8"}
              stroke={open ? "#FFFFFF" : "#1565C0"}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>

      {open && <p className="pb-5 text-[14px] leading-[1.85] text-[#64748B]">{a}</p>}
    </div>
  );
}

function Step({ step, index, last }: { step: NbfcStep; index: number; last: boolean }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 items-start pb-7 transition-all duration-[550ms] ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      {!last && <span className="absolute left-[17px] top-[36px] bottom-0 w-px bg-[#E2E8F0]" />}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] text-[14px] font-bold text-white">
        {step.n}
      </div>
      <div className="pt-1">
        <h4 className="text-[14px] font-semibold text-[#0F172A] mb-2">{step.title}</h4>
        <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{step.body}</p>
      </div>
    </div>
  );
}

export default function NBFCPage() {
  return (
    <main className="bg-white">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pl-4 sm:pl-8 lg:pl-[80px] pr-[30px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] font-medium text-[#64748B] mb-5">
            <Link href="/services" className="text-[#1565C0] hover:text-[#0D47A1] transition-colors duration-200">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <Link href="/services/funding" className="text-[#1565C0] hover:text-[#0D47A1] transition-colors duration-200">
              Funding
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span className="text-[#0F172A]">NBFC Financing</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {HERO_TAGS.map((tag) => (
              <span key={tag} className="rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-semibold text-[#1565C0]">
                {tag}
              </span>
            ))}
          </div>

          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block">
            Funding Assistance — NBFC
          </span>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,44px)] font-extrabold text-[#0F172A] leading-[1.1] mb-6 ">
            Fast, Flexible Business Financing — Beyond Traditional Banking
          </h1>
          <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9 ">
            When banks say no or move too slow, NBFC financing offers a powerful alternative — with faster approvals, flexible eligibility and specialised products designed for businesses with unique funding needs.
          </p>

          <div className="flex flex-wrap gap-4">
            <button className="rounded-sm bg-[#1565C0] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
              GET QUICK FINANCE
            </button>
            <button className="rounded-sm border border-[#E2E8F0] bg-white px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
              CHECK ELIGIBILITY
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=900&q=85"
            alt="NBFC financing"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { v: "48–72 Hrs", l: "Approval Time" },
          { v: "8", l: "NBFC Products" },
          { v: "Low CIBIL", l: "Accepted" },
          { v: "No Collat.", l: "Required" },
        ].map((item, index) => (
          <div key={index} className={cn("py-5 text-center", index < 3 ? "xl:border-r border-white/15" : "")}> 
            <div className="text-[22px] font-black text-white leading-none">{item.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{item.l}</div>
          </div>
        ))}
      </div>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Why NBFC Financing</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,2.8vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                6 Advantages Over Traditional Bank Loans
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
                NBFCs are regulated by the Reserve Bank of India and operate with greater flexibility than commercial banks — making them the ideal choice for businesses that need speed, specialisation, or have non-standard profiles.
              </p>
              <div className="space-y-0">
                {ADVANTAGES.map((advantage, index) => (
                  <AdvantageItem key={advantage.title} advantage={advantage} index={index} />
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <div className="relative w-full h-[280px] sm:h-[380px] lg:h-[480px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
                  alt="NBFC advantage"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute -bottom-6 -left-6 rounded-sm border border-[#E2E8F0] bg-white p-[22px] shadow-[0_8px_32px_rgba(0,0,0,0.10)] min-w-[200px]">
                <div className="text-[30px] font-black text-[#1565C0] leading-none">48 Hrs</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Fastest Approval</div>
                <div className="mt-4 text-[28px] font-black text-[#E65100] leading-none">5–7 Days</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Fund Disbursement</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-[56px]">
              <Eyebrow>NBFC Product Portfolio</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  8 Specialised Financing Products
                </h2>
                <p className="text-[14px] leading-[1.75] text-[#64748B] max-w-[360px]">
                  Each product is designed for a specific business need — we match you with the right one.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PRODUCTS.map((product, index) => (
              <ProductCard key={product.n} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Who Can Apply</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,2.8vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                Flexible & Inclusive Eligibility
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
                NBFC financing is designed to be accessible. Unlike traditional banks, eligibility is assessed holistically — revenue, growth potential and business profile matter more than credit scores alone.
              </p>
              <div className="space-y-4">
                {ELIGIBILITY.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0] text-white flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <p className="text-[13.5px] leading-[1.7] text-[#334155]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative w-full h-[260px] sm:h-[360px] lg:h-[460px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=700&q=80"
                alt="Eligibility"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Our Process</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                7-Step NBFC Financing Process
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
                Most clients receive funds within 5–7 business days of document submission. We manage every step — from matching you with the right NBFC to facilitating rapid disbursement.
              </p>
              <div className="relative w-full h-[160px] sm:h-[200px] lg:h-[260px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=700&q=80"
                  alt="Process"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="space-y-0">
              {STEPS.map((step, index) => (
                <Step key={step.n} step={step} index={index} last={index === STEPS.length - 1} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Frequently Asked Questions</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-6">
                Common Questions About NBFC Financing
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
                Everything you need to know before choosing NBFC financing for your business.
              </p>
              <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=700&q=80"
                  alt="FAQ"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-[#E2E8F0]">
              {FAQS.map((faq, index) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} index={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Get quick finance"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Need Fast Business Financing?</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(26px,3vw,40px)] font-bold text-[#0F172A] leading-[1.15] mb-4">
                Get the funds you need, when you need them.
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9">
                Speak with our funding specialists and discover the best NBFC product for your business, with fast approvals and friendly support.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="rounded-sm bg-[#1565C0] px-8 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
                  APPLY NOW
                </button>
                <button className="rounded-sm border border-[#E2E8F0] bg-white px-8 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
                  CONTACT US
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
